/**
 * useSimilarCompaniesState - Hook for managing comparable/similar companies state
 *
 * Extracted from CompanyDetails.jsx to reduce file size and improve maintainability.
 * Manages all state related to the Similar Companies (Comparables) tab:
 * - View selection (list/heatmap/chart)
 * - Heatmap controls (tile size, color coding, grouping)
 * - Sort options
 * - Loading state and polling
 * - Company navigation (opened similar company)
 * - Save to project popup
 *
 * @module useSimilarCompaniesState
 */

import { useRef } from 'react';
import { useLocalObservable } from 'mobx-react';
import { runInAction } from 'mobx';
import { API_ENDPOINTS } from '../../../../api/endpoints';
import { DEFAULT_COMPARABLES_VIEW } from '../constants/companyDetailsConstants';
import { getTooltipId } from '../../../../utils/utils';
import { PROFILER_VIEWS } from '../../../../utils/constants/profilerViews';
import { UI_OPTIONS, UI_OPTIONS_UTILS } from '../../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import { PROFILER_SEARCH_FIELDS } from '../../../../utils/constants/fields';
import { DETAILS_POUP_TABS } from '../constants/tabs';
import {
  getProfilerSortSelectionBySearchResultsPage,
  getProfilerViewSelectionBySearchResultsPage,
  getProfilerHeatmapTileSizeSelectionBySearchResultsPage,
  getProfilerHeatmapColorCodingSelectionBySearchResultsPage,
  getProfilerHeatmapGroupingToggleBySearchResultsPage,
  hasSavedProfilerHeatmapGroupingToggleBySearchResultsPage,
  saveProfilerSortSelectionBySearchResultsPage,
  saveProfilerViewSelectionBySearchResultsPage,
  saveProfilerHeatmapTileSizeSelectionBySearchResultsPage,
  saveProfilerHeatmapColorCodingSelectionBySearchResultsPage,
  saveProfilerHeatmapGroupingToggleBySearchResultsPage,
  filterHeatmapTileSizeOptionsForAccess,
  filterHeatmapColorCodingOptionsForAccess
} from '../../../../pages/search/SearchResults/constants/constants';
import { isComparablesToProjectSaveDisabled } from '../../../../utils/constants/variables';
import { isRevenueMetric } from '../../../../utils/constants/profilerFinancials';

// Extracted to reduce nesting depth (SonarCloud S3776)
const createAscComparator = (propKey) => (a, b) =>
  +(a[propKey] ?? Number.MAX_SAFE_INTEGER) - +(b[propKey] ?? Number.MAX_SAFE_INTEGER);

const createDescComparator = (propKey) => (a, b) =>
  +(b[propKey] ?? -Number.MAX_SAFE_INTEGER / 3) - +(a[propKey] ?? -Number.MAX_SAFE_INTEGER / 3);

const SORT_COMPARATORS = {
  asc: createAscComparator,
  desc: createDescComparator
};

/**
 * Find the latest revenue entry from financial items.
 * Revenue is identified by the BE metric_group (TD-2401), which unifies the
 * TC/COGS accounting variants and self-disclosed revenue under one dimension —
 * so this now also resolves for COGS-basis and self-disclosed-only companies.
 * @param {Array} items - Financial items array
 * @returns {Object|null} Latest entry or null
 */
const findLatestRevenueEntry = (items) => {
  const item = items?.find(isRevenueMetric);
  if (!item?.entries?.length) return null;
  return item.entries.reduce((latest, entry) => (entry._year > latest._year ? entry : latest));
};

/**
 * Hook for managing similar companies state
 *
 * @param {Object} deps - Dependencies
 * @param {Function} deps.makeRequest - API request function
 * @param {Object} deps.authStore - Auth store (TD-2376: demo counter reconcile)
 * @param {Object} deps.profilerAccessStore - Access control store
 * @param {Object} deps.profilerProjectStore - Project store with saveProject
 * @param {Object} deps.utilsStore - Utils store with lockScroll
 * @param {Function} deps.getObservableCompany - Getter for current company
 * @param {Function} deps.getFinancials - Getter for financials data
 * @param {Function} deps.getCurrentTab - Getter for current tab
 * @param {Function} deps.isRendered - Getter for rendered state
 * @param {boolean} deps.isFromFeedbackProject - Whether from feedback project
 * @param {Object} deps.project - Current project
 * @param {Function} deps.navigateToCompany - URL navigation function for comparable companies
 * @param {string} [deps.initialComparablesId] - TD-1816: Comparables ID for cache restoration on back navigation
 * @param {boolean} [deps.isStandalonePage=false] - True for URL-based pages, enables URL sync for caching
 * @returns {Object} Observable state object
 */
const useSimilarCompaniesState = ({
  makeRequest,
  authStore,
  profilerAccessStore,
  profilerProjectStore,
  utilsStore,
  getObservableCompany,
  getFinancials,
  getCurrentTab,
  isRendered,
  isFromFeedbackProject,
  project,
  navigateToCompany,
  initialComparablesId,
  isStandalonePage = false
}) => {
  // Use ref to hold latest navigateToCompany to avoid stale closure in observable
  const navigateToCompanyRef = useRef(navigateToCompany);
  navigateToCompanyRef.current = navigateToCompany;

  const state = useLocalObservable(() => ({
    // Navigation state - using URL navigation instead of modal
    onSimilarCompanyDetailsClick: (company = {}) => {
      if (navigateToCompanyRef.current) {
        navigateToCompanyRef.current(company);
      }
    },

    // Data state
    similarCompanies: [],
    similarCompaniesTotalCount: 0,

    // Reset state when company changes (for overlay navigation)
    resetForNewCompany: () => {
      clearInterval(state.comparablesPollInterval);
      state.similarCompanies = [];
      state.similarCompaniesTotalCount = 0;
      state.comparablesId = null;
      state.loadingComparablesId = null;
      state.comparableData = null;
      state.similarCompaniesInitialLoad = true;
      state.isLoadingSimilarCompanies = false;
      state.loadSimilarCompaniesError = null;
      state.isSimilarCompaniesLoadSuspend = false;
      state.triedCachedLoad = false;
      state.isComparableCompaniesTriggerSuccessful = false;
    },

    // Suppress initial load without resetting data (for back navigation without cached ID)
    // Prevents fresh search trigger when returning to a company that didn't have cached comparables
    suppressInitialLoad: () => {
      state.similarCompaniesInitialLoad = false;
      state.isSimilarCompaniesLoadSuspend = true;
    },

    // View selection
    selectedSimilarCompaniesView: DEFAULT_COMPARABLES_VIEW,
    selectSimilarCompaniesView: (value = '') => {
      state.selectedSimilarCompaniesView = value;
      saveProfilerViewSelectionBySearchResultsPage(value, state.comparablesId);
      state.scrollToSimilarCompaniesTabsWrap(true);
    },
    isSimilarCompaniesViewMenuToggled: false,
    toggleSimilarCompaniesViewMenu: () => {
      state.isSimilarCompaniesViewMenuToggled = !state.isSimilarCompaniesViewMenuToggled;
      state.scrollToSimilarCompaniesTabsWrap();
    },

    // Heatmap tile size
    profilerHeatmapTileSize: UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].Sales,
    setProfilerHeatmapTileSize: (newValue) => {
      state.profilerHeatmapTileSize = newValue;
      saveProfilerHeatmapTileSizeSelectionBySearchResultsPage(newValue, state.comparablesId);
    },
    isProfilerHeatmapTileSizeMenuToggled: false,
    toggleProfilerHeatmapTileSizeMenu: () => {
      state.isProfilerHeatmapTileSizeMenuToggled = !state.isProfilerHeatmapTileSizeMenuToggled;
    },
    get profilerHeatmapTileSizeOptions() {
      return filterHeatmapTileSizeOptionsForAccess(
        UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE],
        !!profilerAccessStore.canViewProfilerDetailsExtendedData?.(project)
      );
    },

    // Heatmap color coding
    // TD-2499: Lite default colour-coding is CAGR 3Y (no EBIT margin); Full keeps EBIT Margin.
    profilerHeatmapColorCoding: profilerAccessStore.canViewProfilerDetailsExtendedData?.(project)
      ? UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['EBIT Margin (%)']
      : UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 3Y (%)'],
    setProfilerHeatmapColorCoding: (newValue) => {
      state.profilerHeatmapColorCoding = newValue;
      saveProfilerHeatmapColorCodingSelectionBySearchResultsPage(newValue, state.comparablesId);
    },
    isProfilerHeatmapColorCodingMenuToggled: false,
    toggleProfilerHeatmapColorCodingMenu: () => {
      state.isProfilerHeatmapColorCodingMenuToggled =
        !state.isProfilerHeatmapColorCodingMenuToggled;
    },
    get profilerHeatmapColorCodingOptions() {
      return filterHeatmapColorCodingOptionsForAccess(
        UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING],
        !!profilerAccessStore.canViewProfilerDetailsExtendedData?.(project)
      );
    },

    // Heatmap grouping
    isProfilerHeatmapGroupingToggled: false,
    toggleProfilerHeatmapGrouping: () => {
      state.isProfilerHeatmapGroupingToggled = !state.isProfilerHeatmapGroupingToggled;
      saveProfilerHeatmapGroupingToggleBySearchResultsPage(
        state.isProfilerHeatmapGroupingToggled,
        state.comparablesId
      );
      state.toggleProfilerHeatmapGroupingCallback(state.isProfilerHeatmapGroupingToggled);
    },
    toggleProfilerHeatmapGroupingCallback: () => {},
    setToggleProfilerHeatmapGroupingCallback: (func = () => {}) => {
      runInAction(() => {
        state.toggleProfilerHeatmapGroupingCallback = func;
      });
    },
    get profilerHeatmapGroup() {
      if (!state.isProfilerHeatmapGroupingToggled || state.disableProfilerHeatmapGroup) {
        return null;
      }
      return 'scoreLabel';
    },
    get disableProfilerHeatmapGroup() {
      return new Set(state.sortedSimilarCompanies.map((company) => company.scoreLabel)).size < 2;
    },

    // Scroll helper
    scrollToSimilarCompaniesTabsWrap: (force = false) => {
      setTimeout(() => {
        const el = document.getElementById('similar-tabs-scroll-starting-point');
        if (el) {
          const newTop = el.offsetTop + 20;
          if (window.scrollY < newTop || force) {
            window.scrollTo({
              top: newTop,
              behavior: 'smooth'
            });
          }
        }
      }, 100);
    },

    // Sort selection
    selectedSimilarCompaniesSort: 'default',
    selectSimilarCompaniesSortOption: (value = '') => {
      state.selectedSimilarCompaniesSort = value;
      saveProfilerSortSelectionBySearchResultsPage(value, state.comparablesId);
      state.scrollToSimilarCompaniesTabsWrap(true);
    },
    isSimilarCompaniesSortToggled: false,
    toggleSimilarCompaniesSort: () => {
      state.isSimilarCompaniesSortToggled = !state.isSimilarCompaniesSortToggled;
      if (state.isSimilarCompaniesSortToggled) {
        state.scrollToSimilarCompaniesTabsWrap();
      }
    },

    // Computed: sorted companies
    get sortedSimilarCompanies() {
      const results = state.similarCompanies.slice();

      if (state.selectedSimilarCompaniesSort === 'name-asc') {
        results.sort((a, b) =>
          (a.name || a.companyName || '').localeCompare(b.name || b.companyName || '', 'en', {
            sensitivity: 'base'
          })
        );
      } else if (state.selectedSimilarCompaniesSort === 'name-desc') {
        results.sort((a, b) =>
          (b.name || b.companyName || '').localeCompare(a.name || a.companyName || '', 'en', {
            sensitivity: 'base'
          })
        );
      } else if (state.selectedSimilarCompaniesSort !== 'default') {
        const [propKey, typeSort] = state.selectedSimilarCompaniesSort.split('-');
        results.sort(SORT_COMPARATORS[typeSort](propKey));
      }

      return results;
    },

    // Computed: bar chart data
    get similarCompaniesBarChart() {
      return [state.comparableData, ...state.sortedSimilarCompanies];
    },

    // Comparable data and tooltip IDs
    comparableData: null,
    comparableDataGrossProfitTooltipId: getTooltipId(),
    comparableDataDescriptionTooltipId: getTooltipId(),
    comparableDataBasicRevenueTooltipId: getTooltipId(),
    comparableDataBasicEmployeesTooltipId: getTooltipId(),

    // Loading state
    loadingComparablesId: null,
    comparablesId: null,
    isComparableCompaniesTriggerSuccessful: false,
    similarCompaniesInitialLoad: true,
    isLoadingSimilarCompanies: false,
    loadSimilarCompaniesError: null,
    isSimilarCompaniesLoadSuspend: false,
    comparablesPollInterval: null,
    triedCachedLoad: false, // TD-1816: Track if we've tried loading from cache

    // TD-2388: a comparables search consumes a demo credit on the BE at TRIGGER
    // time, but the FE only reconciled on poll completion — which is cancelled
    // when the user navigates away mid-poll. Reconcile from the BE whenever the
    // trigger settles so the credit shows without waiting for completion or an
    // F5. Read-only (GET /get-user-info), so it can never over-count; demo-gated.
    reconcileDemoCounter: () => {
      if (profilerAccessStore.isProfilerDemo) {
        authStore?.refreshProfilerDemoCounter();
      }
    },

    // Helper: Handle poll completed response (extracted to reduce nesting)
    handlePollCompleted: (pollResponse, comparablesId) => {
      clearInterval(state.comparablesPollInterval);
      const similarCompaniesResults = pollResponse.results || [];
      state.similarCompanies = profilerAccessStore.isProfilerDemo
        ? similarCompaniesResults.slice(0, 10)
        : similarCompaniesResults;
      state.similarCompaniesTotalCount = pollResponse.totalCount || similarCompaniesResults.length;
      state.comparablesId = comparablesId;
      state.comparableData = pollResponse.comparableData;

      // TD-2376/TD-2388: reconcile the demo counter on completion too (safety
      // net alongside the trigger-time reconcile). Note this also fires on cached
      // comparable loads (no credit consumed) — a harmless extra GET.
      state.reconcileDemoCounter();

      // Sync comparablesId to URL for back navigation caching (standalone pages only)
      if (isStandalonePage && typeof globalThis !== 'undefined' && globalThis.location) {
        const url = new URL(globalThis.location.href);
        url.searchParams.set('comparablesId', comparablesId);
        globalThis.history.replaceState({}, '', url.toString());
      }
      state.isLoadingSimilarCompanies = false;
      state.isSimilarCompaniesLoadSuspend = false;
      state.similarCompaniesInitialLoad = false;

      runInAction(() => {
        state.selectedSimilarCompaniesSort =
          getProfilerSortSelectionBySearchResultsPage(comparablesId);
        // TD-2838: pass this tab's default explicitly. Without it the getter falls
        // back to the shared DEFAULT_PROFILER_VIEW, which is the heatmap — and since
        // the comparables views are exclusive, that hid the table on every result set
        // with no stored preference.
        state.selectedSimilarCompaniesView = getProfilerViewSelectionBySearchResultsPage(
          comparablesId,
          DEFAULT_COMPARABLES_VIEW
        );
        state.profilerHeatmapTileSize =
          getProfilerHeatmapTileSizeSelectionBySearchResultsPage(comparablesId);
        state.profilerHeatmapColorCoding =
          getProfilerHeatmapColorCodingSelectionBySearchResultsPage(comparablesId);
        state.isProfilerHeatmapGroupingToggled =
          getProfilerHeatmapGroupingToggleBySearchResultsPage(comparablesId);

        // TD-2499: Lite can't use the (EBIT-centric) chart view, the EBIT tile size,
        // or the EBIT-margin colour coding — coerce any restored/persisted value it
        // can't see so the heatmap/view stays valid.
        if (!profilerAccessStore.canViewProfilerDetailsExtendedData?.(project)) {
          if (state.selectedSimilarCompaniesView === PROFILER_VIEWS.CHART) {
            state.selectedSimilarCompaniesView = DEFAULT_COMPARABLES_VIEW;
          }
          if (
            state.profilerHeatmapTileSize === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].EBIT
          ) {
            state.profilerHeatmapTileSize = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].Sales;
          }
          if (
            state.profilerHeatmapColorCoding ===
            UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['EBIT Margin (%)']
          ) {
            state.profilerHeatmapColorCoding =
              UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 3Y (%)'];
          }
        }
      });

      // Enable grouping if applicable. Deferred via setTimeout(...,0) so it runs
      // after the runInAction selection assignments above are applied. Hoisted
      // out of the runInAction callback and delegated to a sibling method so the
      // function nesting stays within 4 levels (SonarQube S2004).
      setTimeout(() => state.maybeEnableHeatmapGrouping(comparablesId), 0);

      if (getCurrentTab() === DETAILS_POUP_TABS.SIMILAR_COMPANIES) {
        state.scrollToSimilarCompaniesTabsWrap(true);
      }
    },

    // Helper: Handle poll failure (extracted to reduce nesting)
    handlePollFailed: (errorMessage) => {
      clearInterval(state.comparablesPollInterval);
      state.loadSimilarCompaniesError = errorMessage || 'Failed to load similar companies.';
      state.isLoadingSimilarCompanies = false;
      state.isSimilarCompaniesLoadSuspend = false;
      state.similarCompaniesInitialLoad = false;
    },

    // Helper: enable heatmap grouping after a completed poll (extracted so the
    // deferred callback in handlePollCompleted stays within 4 nesting levels).
    maybeEnableHeatmapGrouping: (comparablesId) => {
      if (hasSavedProfilerHeatmapGroupingToggleBySearchResultsPage(comparablesId)) {
        return;
      }
      if (!state.isProfilerHeatmapGroupingToggled && !state.disableProfilerHeatmapGroup) {
        state.toggleProfilerHeatmapGrouping();
      }
    },

    // Helper: handle a successful comparable-companies trigger — start the 5s
    // poll loop. Extracted from loadSimilarCompanies.onSuccess so the poll
    // scheduling isn't nested >4 functions deep (SonarQube S2004).
    handleComparablesTriggerSuccess: (response) => {
      // TD-2388: a valid trigger response means the BE already spent a demo
      // credit — reconcile now, before the isRendered guard, so it lands even if
      // the user navigated away before the trigger resolved (the completion
      // reconcile below is cancelled on unmount).
      if (response?.comparablesId) {
        state.reconcileDemoCounter();
      }
      if (!isRendered() || !response?.comparablesId) {
        return;
      }
      state.isComparableCompaniesTriggerSuccessful = true;
      state.loadingComparablesId = response.comparablesId;
      state.comparablesPollInterval = setInterval(
        () => state.pollComparablesResults(response.comparablesId),
        5_000
      );
    },

    // Helper: one poll tick for comparable-companies results. Extracted to a
    // sibling method (was an inline closure nested 5 levels deep). Preserves the
    // clearInterval + !isRendered() teardown that stops the loop on unmount.
    pollComparablesResults: (comparablesId) => {
      makeRequest({
        endpoint: API_ENDPOINTS.POLL_PROFILER_COMPARABLE_COMPANIES,
        body: { comparablesId },
        onSuccess: (pollResponse) => {
          if (!isRendered()) {
            clearInterval(state.comparablesPollInterval);
            return;
          }
          if (pollResponse.status === 'completed') {
            state.handlePollCompleted(pollResponse, comparablesId);
          } else if (pollResponse.status === 'failed') {
            state.handlePollFailed('Failed to load similar companies.');
          }
          // status === 'pending' → do nothing, wait for next poll
        },
        onError: (pollErrorMessage) => {
          if (!isRendered()) {
            clearInterval(state.comparablesPollInterval);
            return;
          }
          state.handlePollFailed(pollErrorMessage);
        }
      });
    },

    // TD-1816: Load cached comparable companies using existing comparablesId
    loadCachedSimilarCompanies: (cachedComparablesId) => {
      if (state.isSimilarCompaniesLoadSuspend || !cachedComparablesId) {
        return false;
      }

      state.isSimilarCompaniesLoadSuspend = true;
      state.isLoadingSimilarCompanies = true;
      state.similarCompaniesInitialLoad = false; // Prevent initial load effect from triggering
      state.loadSimilarCompaniesError = null;
      state.loadingComparablesId = cachedComparablesId;

      // Directly poll for cached results (skip triggering new search)
      makeRequest({
        endpoint: API_ENDPOINTS.POLL_PROFILER_COMPARABLE_COMPANIES,
        body: { comparablesId: cachedComparablesId },
        onSuccess: (pollResponse) => {
          if (!isRendered()) return;
          if (pollResponse.status === 'completed') {
            state.isComparableCompaniesTriggerSuccessful = true;
            state.handlePollCompleted(pollResponse, cachedComparablesId);
          } else if (pollResponse.status === 'failed' || pollResponse.status === 'pending') {
            // Cache miss or still pending - fall back to fresh load
            state.isSimilarCompaniesLoadSuspend = false;
            state.loadSimilarCompanies();
          }
        },
        onError: () => {
          if (!isRendered()) return;
          // Cache miss - fall back to fresh load
          state.isSimilarCompaniesLoadSuspend = false;
          state.loadSimilarCompanies();
        }
      });

      return true;
    },

    // Main loading function
    // TD-1816: Check for cached comparablesId first before triggering new search
    loadSimilarCompanies: () => {
      if (state.isSimilarCompaniesLoadSuspend) {
        return;
      }

      // TD-1816: Try to load from cache first if initialComparablesId was provided
      if (initialComparablesId && !state.triedCachedLoad) {
        state.triedCachedLoad = true;
        if (state.loadCachedSimilarCompanies(initialComparablesId)) {
          return; // Loading from cache, will fall back to fresh load if needed
        }
      }

      state.isSimilarCompaniesLoadSuspend = true;
      state.isLoadingSimilarCompanies = true;
      state.loadSimilarCompaniesError = null;

      const observableCompany = getObservableCompany();

      makeRequest({
        endpoint: API_ENDPOINTS.TRIGGER_PROFILER_COMPARABLE_COMPANIES,
        body: {
          companyId: observableCompany.id,
          ...(isFromFeedbackProject
            ? { sharedProjectId: project.id, sharedProjectOwner: project.ownerEmail }
            : {})
        },
        onSuccess: (response) => state.handleComparablesTriggerSuccess(response),
        onError: (errorMessage) => {
          // TD-2388: the BE spends the demo credit at trigger time, before the
          // downstream compute that may fail. Reconcile even on error (and before
          // the isRendered guard) so a credit spent on a failed comparables run is
          // still reflected.
          state.reconcileDemoCounter();
          if (!isRendered()) {
            return;
          }

          state.loadSimilarCompaniesError = errorMessage || 'Failed to load similar companies.';
          state.isLoadingSimilarCompanies = false;
          state.isSimilarCompaniesLoadSuspend = false;
          state.similarCompaniesInitialLoad = false;
        },
        onFinally: () => {
          // Left the function as it's invoked in few places,
          // and it's not clear if it's needed to be invoked in the current context
        }
      });
    },

    // Computed: criteria fields for loader display
    get comparablesLoaderCriteriaFields() {
      const fields = {};
      const financials = getFinancials();
      const observableCompany = getObservableCompany();

      const consolidatedProfitAndLoss = financials?.details?.consolidated?.groups?.find(
        (g) => g.id === 'profitAndLoss'
      );
      const individualProfitAndLoss = financials?.details?.individual?.groups?.find(
        (g) => g.id === 'profitAndLoss'
      );

      const consolidatedLatestRevenueEntry = findLatestRevenueEntry(
        consolidatedProfitAndLoss?.items
      );
      const individualLatestRevenueEntry = findLatestRevenueEntry(individualProfitAndLoss?.items);

      let originalRevenue;

      if (consolidatedLatestRevenueEntry && individualLatestRevenueEntry) {
        originalRevenue =
          individualLatestRevenueEntry._year + 1 > consolidatedLatestRevenueEntry._year
            ? individualLatestRevenueEntry.value
            : consolidatedLatestRevenueEntry.value;
      } else if (consolidatedLatestRevenueEntry) {
        originalRevenue = consolidatedLatestRevenueEntry.value;
      } else if (individualLatestRevenueEntry) {
        originalRevenue = individualLatestRevenueEntry.value;
      } else {
        originalRevenue = observableCompany._revenue;
      }

      // TD-1809: Handle null/undefined revenue - leave fields empty instead of NaN
      const hasValidRevenue = originalRevenue != null && !Number.isNaN(originalRevenue);
      const minRevenue = hasValidRevenue ? ((originalRevenue * 0.25) / 1_000_000).toFixed(2) : '';
      const maxRevenue = hasValidRevenue ? ((originalRevenue * 2) / 1_000_000).toFixed(2) : '';

      fields[PROFILER_SEARCH_FIELDS.REVENUE_MIN.NAME] = minRevenue;
      fields[PROFILER_SEARCH_FIELDS.REVENUE_MAX.NAME] = maxRevenue;
      fields[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME] = (observableCompany.industries || []).join(
        ', '
      );
      fields[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.NAME] = (observableCompany.valueChain || []).join(
        ', '
      );
      fields[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.NAME] = (
        observableCompany.products || []
      ).join(', ');

      // Also expose with friendlier names for tests
      fields.industry = fields[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME];
      fields.valueChain = fields[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.NAME];
      fields.revenueMin = fields[PROFILER_SEARCH_FIELDS.REVENUE_MIN.NAME];
      fields.revenueMax = fields[PROFILER_SEARCH_FIELDS.REVENUE_MAX.NAME];

      return fields;
    },

    // Save search popup
    saveProfilerComparablesSearchPopupDisplayed: null,
    toggleProfilerComparablesSaveProjectPopup: (entry) => {
      state.saveProfilerComparablesSearchPopupDisplayed = entry;
      if (entry) {
        utilsStore.lockScroll(true);
      } else {
        utilsStore.lockScroll(false);
      }
    },
    isProfilerComparablesSavingSearch: false,
    onProfilerComparablesSaveSearch: (searchName, searchReason, onSuccess, onError, onFinally) => {
      state.isProfilerComparablesSavingSearch = true;
      profilerProjectStore.saveProject(
        state.comparablesId,
        searchName,
        searchReason,
        onSuccess,
        onError,
        () => {
          state.isProfilerComparablesSavingSearch = false;
          onFinally();
        }
      );
    },

    // Computed: project ID from comparables
    get comparablesProjectId() {
      return profilerProjectStore.projects.find((p) => p.searchId === state.comparablesId)?.id;
    },

    // ============================================
    // Save as Project Button State
    // ============================================
    get isSaveComparablesAsProjectDisabled() {
      return (
        isComparablesToProjectSaveDisabled ||
        !profilerAccessStore.canUseProfilerSearch ||
        state.isProfilerComparablesSavingSearch ||
        profilerProjectStore.isLoadingProjectsData ||
        profilerProjectStore.loadProjectsError ||
        !!state.comparablesProjectId
      );
    },
    get saveComparablesAsProjectTooltip() {
      if (state.isSimilarCompaniesViewMenuToggled || state.isSimilarCompaniesSortToggled) {
        return null;
      }
      if (isComparablesToProjectSaveDisabled) {
        return <>Upcoming feature</>;
      }
      if (profilerAccessStore.canUseProfilerSearch) {
        if (state.comparablesProjectId) {
          return <>Already saved as project</>;
        }
        return <>Save as project</>;
      }
      return (
        <>
          Requires Profiler Lite or Profiler Full access.
          <br />
          Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
          assistance.
        </>
      );
    },
    get saveComparablesAsProjectTooltipClickable() {
      return !isComparablesToProjectSaveDisabled && !profilerAccessStore.canUseProfilerSearch;
    },
    get saveComparablesAsprojectTooltipPosition() {
      if (profilerAccessStore.canUseProfilerSearch) {
        return 'bottom-center';
      }
      return 'bottom-end';
    }
  }));

  return state;
};

export default useSimilarCompaniesState;
